<template>
  <div v-redirect-if-no-permission:ACCOUNTING :key="switch1">
    <loading-component :value="loading" />

    <!-- Desktop Table View -->
    <div class="desktop-view">
      <v-toolbar flat color="white">
        <v-toolbar-title>{{ $t("accountingPage.transferCentersDonePaymentsTitle") }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="search"
          :label='$t("accountingPage.search")'
          single-line
          hide-details
          @keyup.enter="getAccountingData"
          @click:append="getAccountingData"
        ></v-text-field>
      </v-toolbar>

      <v-data-table :headers="headers" :items="accountingData" class="elevation-1" :pagination.sync="pagination"
        :rows-per-page-items="[5, 10, 15, 25, 100]" :loading="loading" :total-items="totalItems">
        <template v-slot:items="props">
          <td class="text-xs-start">{{ props.item.id }}</td>
          <td class="text-xs-start">{{ props.item.name }}</td>
          <td class="text-xs-start">{{ props.item.phone_number }}</td>
          <td class="text-xs-start">{{ props.item.username }}</td>
          <td class="text-xs-start">{{ props.item.balance | money }}</td>
          <td class="text-xs-center">
            <v-btn
              v-if="hasPermission('TRANSFER_CENTERS')"
              small
              icon
              @click="goToTransferCenterProfile(props.item)"
            >
              <v-icon>account_circle</v-icon>
            </v-btn>
            <v-btn
              small
              icon
              @click="openTransferDialog(props.item)"
            >
              <v-icon>monetization_on</v-icon>
            </v-btn>
          </td>
        </template>
      </v-data-table>
    </div>

    <!-- Mobile Card View -->
    <div class="mobile-view">
      <v-toolbar flat color="white">
        <v-text-field
          v-model="search"
          append-icon="search"
          :label='$t("accountingPage.search")'
          single-line
          hide-details
          @keyup.enter="getAccountingData"
          @click:append="getAccountingData"
        ></v-text-field>
      </v-toolbar>

      <div v-if="loading" class="text-center pa-4">
      </div>

      <div v-else-if="accountingData.length === 0" class="text-center pa-4">
        <b>{{ $t("accountingPage.noData") }}</b>
      </div>

      <div v-else class="cards-container">
        <v-card
          v-for="item in accountingData"
          :key="item.id"
          class="mb-3 payment-card"
          elevation="2"
        >
          <v-card-text>
            <div class="card-row">
              <span class="card-label">#:</span>
              <span class="card-value">{{ item.id }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">{{ $t("accountingPage.name") }}:</span>
              <span class="card-value">{{ item.name || '-' }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">{{ $t("accountingPage.phone") }}:</span>
              <span class="card-value">{{ item.phone_number || '-' }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">{{ $t("accountingPage.email") }}:</span>
              <span class="card-value">{{ item.username || '-' }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">{{ $t("accountingPage.balance") }}:</span>
              <span class="card-value balance" :class="item.balance >= 0 ? 'positive' : 'negative'">
                {{ item.balance | money }}
              </span>
            </div>
          </v-card-text>
          <v-card-actions class="card-actions">
            <v-btn
              v-if="hasPermission('TRANSFER_CENTERS')"
              small
              icon
              @click="goToTransferCenterProfile(item)"
            >
              <v-icon>account_circle</v-icon>
            </v-btn>
            <v-btn
              small
              icon
              @click="openTransferDialog(item)"
            >
              <v-icon>monetization_on</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>

      <div v-if="accountingData.length > 0" class="pagination-container">
        <v-pagination
          v-model="pagination.page"
          :length="Math.ceil(totalItems / pagination.rowsPerPage)"
          @input="getAccountingData"
          :total-visible="5"
        ></v-pagination>
      </div>
    </div>

    <v-dialog v-model="dialog" max-width="500px">
      <v-card style="padding: 10px;">
        <v-card-title>
          <span class="headline">
            {{ $t("accountingPage.addTransfer") }}
          </span>
           <v-spacer></v-spacer>
          <v-btn icon @click="dialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-text-field :rules="[rules.required, rules.number]" :label='$t("accountingPage.amount")' type="number" v-model="paidAmount"
            :placeholder='$t("accountingPage.amount")' />
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" style="width: 50%;" :loading="dialogLoading" flat @click="pay(0)">{{ $t("accountingPage.removeBalance") }}</v-btn>
          <v-btn color="primary" style="width: 50%;" :loading="dialogLoading" flat @click="pay(1)">{{ $t("accountingPage.addBalance") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DataTable from "@/components/monaco/DataTable";
import LoadingComponent from "@/components/LoadingComponent";

export default {
  name: "TransferCenterDonePayments",
  components: {
    LoadingComponent,
    DataTable
  },
  data() {
    return {
      rules: {
        length: (len) => (v) =>
          (v || "").length >= len || this.$t("accountingPage.invalidLength", { len }),
        required: (v) => !!v || this.$t("accountingPage.requiredField"),
        number: (v) => /^\d+(\.\d+)?$/.test(v) || this.$t("accountingPage.positiveNumber")
      },
      accountingData: [],
      loading: false,
      dialogLoading: false,
      dialog: false,
      selectedTransferCenter: null,
      paidAmount: 0,
      search: '',
      totalItems: 0,
      pagination: {
        sortBy: "id",
        rowsPerPage: 25,
        page: 1,
      },
      switch1: null,
      isInitialLoad: true,
    };
  },
  computed: {
    headers() {
      return [
        { text: "#", value: "id" },
        { text: this.$t("accountingPage.name"), value: "name" },
        { text: this.$t("accountingPage.phone"), value: "phone_number" },
        { text: this.$t("accountingPage.email"), value: "username" },
        { text: this.$t("accountingPage.balance"), value: "balance" },
        { text: this.$t("accountingPage.actions"), value: "actions", sortable: false, align: "center" }
      ];
    },
  },
  mounted() {
    this.getAccountingData();
    this.$nextTick(() => {
      this.isInitialLoad = false;
    });
  },
  watch: {
    pagination: {
      handler() {
        if (!this.isInitialLoad) {
          this.getAccountingData();
        }
      },
      deep: true,
    },
    dialog: function (val) {
      this.paidAmount = 0;
      this.dialogLoading = false;
      if (!val)
        this.selectedTransferCenter = null;
    },
  },
  methods: {
    goToTransferCenterProfile(item) {
      this.$router.push({
        path: "/transfer-centers/" + item.id,
      });
    },
    openTransferDialog(item) {
      this.selectedTransferCenter = item;
      this.dialog = true;
    },
    getAccountingData() {
      this.loading = true;
      const params = new URLSearchParams();
      params.append("perPage", String(this.pagination.rowsPerPage));
      params.append("page", this.pagination.page);
      params.append("sortBy", "id");
      params.append("descending", "false");
      if (this.search) {
        params.append("search", this.search);
      }

      this.axios
        .get("transfer-center-balance", { params })
        .then(({ data }) => {
          const responseData = data.data || {};
          this.accountingData = responseData.data || [];
          this.totalItems = responseData.total || 0;
          if (responseData.per_page) {
            this.pagination.rowsPerPage = Number(responseData.per_page);
          }
        })
        .catch(() => {
          this.accountingData = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    pay(to) {
      if (!this.paidAmount || this.paidAmount <= 0)
        return;
      let paid = this.paidAmount;
      if (to == 0)
        paid = -this.paidAmount;
      this.dialogLoading = true;
      this.axios
        .post(`transfer-center/transfer`, {
          transfer_center_id: this.selectedTransferCenter.id,
          amount: this.toStoredAmount(paid)
        })
        .then(() => {
          window.getApp.$emit(
            "SHOW_TOAST",
            this.$t("accountingPage.transferAdded"),
            "green"
          );
          this.getAccountingData();
        })
        .catch((err) => {
          window.getApp.$emit("SHOW_TOAST", err.response.data.message || this.$t("accountingPage.errorOccurred"), "red");
        }).finally(() => {
          this.dialog = false;
        });
    },
  },
};
</script>

<style scoped>
@media (max-width: 960px) {
  .desktop-view {
    display: none;
  }
}

@media (min-width: 961px) {
  .mobile-view {
    display: none;
  }
}

.cards-container {
  padding: 16px;
}

.payment-card {
  width: 100%;
}

.card-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.card-row:last-child {
  border-bottom: none;
}

.card-label {
  font-weight: 600;
  color: #666;
  font-size: 14px;
}

.card-value {
  color: #333;
  font-size: 14px;
  text-align: end;
}

.card-value.balance {
  font-weight: 700;
  font-size: 16px;
}

.card-value.balance.positive {
  color: #4caf50;
}

.card-value.balance.negative {
  color: #f44336;
}

.card-actions {
  padding: 8px 16px;
  justify-content: flex-end;
}

.pagination-container {
  padding: 16px;
  display: flex;
  justify-content: center;
}
</style>
